/*
 * The shift buttons on the Schedule Hours page (as CGL's). A night shift is two windows - 22:00-24:00 on the
 * day and 00:00-06:00 on the next - never one that runs backwards, so the buttons write both halves.
 */

export interface Shift {
  startMin: number;
  endMin: number;
}

export type PresetKey = 'day' | 'back' | 'night';

export const SHIFT_PRESETS: readonly { key: PresetKey; label: string; title: string; today: Shift[]; next: Shift[] }[] = [
  { key: 'day', label: 'Day', title: 'Day shift, 06:00–14:00', today: [{ startMin: 360, endMin: 840 }], next: [] },
  { key: 'back', label: 'Back', title: 'Back shift, 14:00–22:00', today: [{ startMin: 840, endMin: 1320 }], next: [] },
  { key: 'night', label: 'Night', title: 'Night shift, 22:00–06:00, carried into the next day',
    today: [{ startMin: 1320, endMin: 1440 }], next: [{ startMin: 0, endMin: 360 }] },
];

/** As the server: at most 4 shifts a day. */
export const MAX_SHIFTS = 4;

const same = (a: Shift, b: Shift) => a.startMin === b.startMin && a.endMin === b.endMin;
const has = (list: Shift[], s: Shift) => list.some((x) => same(x, s));
const sorted = (list: Shift[]) => [...list].sort((a, b) => a.startMin - b.startMin);

export function overlaps(list: Shift[]): boolean {
  const s = sorted(list);
  return s.some((x, i) => i > 0 && x.startMin < s[i - 1].endMin);
}

/** Which presets a day already works (all of a preset's windows present). */
export function presetsOn(today: Shift[], next: Shift[]): Set<PresetKey> {
  return new Set(SHIFT_PRESETS
    .filter((p) => p.today.every((w) => has(today, w)) && p.next.every((w) => has(next, w)))
    .map((p) => p.key));
}

/**
 * Switches one preset on or off for a day, leaving its other shifts (Day then Back = a double shift).
 * Returns null when switching it on would overlap a shift the day already has, or give a day more than 4.
 */
export function togglePreset(today: Shift[], next: Shift[], key: PresetKey): { today: Shift[]; next: Shift[]; nextChanged: boolean } | null {
  const p = SHIFT_PRESETS.find((x) => x.key === key);
  if (presetsOn(today, next).has(key)) {
    return {
      today: today.filter((s) => !has(p.today, s)),
      next: next.filter((s) => !has(p.next, s)),
      nextChanged: p.next.length > 0,
    };
  }
  // a half already there (e.g. 22:00-24:00 left after tomorrow's 00:00-06:00 was removed) is kept once
  const t = sorted([...today, ...p.today.filter((w) => !has(today, w))]);
  const n = sorted([...next, ...p.next.filter((w) => !has(next, w))]);
  if (overlaps(t) || overlaps(n) || t.length > MAX_SHIFTS || n.length > MAX_SHIFTS) {
    return null;
  }
  return { today: t, next: n, nextChanged: p.next.length > 0 };
}

/** A custom shift from the Block of days panel: an end at or before the start runs over midnight. */
export function splitAcrossMidnight(startMin: number, endMin: number): { today: Shift[]; next: Shift[] } {
  if (endMin > startMin) {
    return { today: [{ startMin, endMin }], next: [] };
  }
  return { today: [{ startMin, endMin: 1440 }], next: endMin > 0 ? [{ startMin: 0, endMin }] : [] };
}
